import { useContext, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import ComplianceRing from './ComplianceRing'
import ComplianceItemCard from './ComplianceItemCard'
import AssignComplianceDialog from './AssignComplianceDialog'
import ComplianceEditDialog from '../../SociedadDetalle/ComplianceEditDialog'
import DocumentPreviewDialog from './DocumentPreviewDialog'
import DeleteDialog from '../../../DeleteDialog'
import CPEditIconButton from '../../../Utils/CPEditIconButton'
import CPDeleteIconButton from '../../../Utils/CPDeleteIconButton'
import { getCountryLabel } from '../../../../Utils/getCountryLabel'
import { authContext } from '../../../../contexts/AuthContext'
import { updateSocietyCompliance, deleteSocietyCompliance } from '../../../../services/societyCompliances'
import {
  listSocietyComplianceDocuments,
  unlinkSocietyComplianceDocument,
  deleteSocietyComplianceDocument
} from '../../../../services/societyComplianceDocuments'
import { getSocietyLiteralDocument, generateSocietyLiteralDocumentUrl } from '../../../../services/societyDocuments'

export default function SocietyCard({ society, clientId, canEdit, refreshToken, onEdit, onDelete, onChange }) {
  const { t } = useTranslation()
  const { auth } = useContext(authContext)
  const [countryLabel, setCountryLabel] = useState('')
  const [compliancesOpen, setCompliancesOpen] = useState(false)
  const [assignOpen, setAssignOpen] = useState(false)
  const [editingCompliance, setEditingCompliance] = useState(null)
  const [deleteComplianceCandidate, setDeleteComplianceCandidate] = useState({})
  const [literalDoc, setLiteralDoc] = useState(null)
  const [literalPreviewOpen, setLiteralPreviewOpen] = useState(false)

  useEffect(() => {
    let active = true
    getCountryLabel(society.country, i18next.language).then((label) => {
      if (active) setCountryLabel(label)
    })
    return () => {
      active = false
    }
  }, [society.country])

  useEffect(() => {
    let active = true
    getSocietyLiteralDocument(society.id)
      .then((doc) => {
        if (active) setLiteralDoc(doc || null)
      })
      .catch(() => {
        if (active) setLiteralDoc(null)
      })
    return () => {
      active = false
    }
  }, [society.id, refreshToken])

  const handleUpdateCompliance = async (item) => {
    await updateSocietyCompliance({
      id: item.id,
      status: item.status,
      due_date: item.due_date,
      notifyClient: !!item.notify_client,
      loggedUserId: auth.user.id
    })
    setEditingCompliance(null)
    onChange()
  }

  const handleDeleteCompliance = async (item) => {
    try {
      const documents = (await listSocietyComplianceDocuments(item.id)) || []
      await Promise.all(
        documents.map((doc) =>
          doc.is_linked
            ? unlinkSocietyComplianceDocument(doc.id, auth.user.id)
            : deleteSocietyComplianceDocument(doc.id, auth.user.id)
        )
      )
    } catch (err) {
      console.error('Error clearing compliance documents before delete:', err)
    }
    await deleteSocietyCompliance(item.id, auth.user.id)
    onChange()
  }

  return (
    <Card
      variant='outlined'
      sx={{
        borderRadius: 3,
        p: { xs: 2, md: 3 },
        borderColor: 'divider'
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
        <ComplianceRing compliances={society.compliances} />

        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <BusinessRoundedIcon sx={{ color: window.$brand === 'PRB' ? '#17afb6' : '#102a46' }} />
            <Typography variant='h6' sx={{ fontWeight: 800 }}>
              {society.name || society.legal_id}
            </Typography>
            {society.name && (
              <Chip size='small' variant='outlined' label={society.legal_id} />
            )}
            <Chip size='small' label={society.type} sx={{ fontWeight: 600 }} />
            <Chip
              size='small'
              label={t(society.current_status === 'active' ? 'Active' : 'Inactive')}
              color={society.current_status === 'active' ? 'success' : 'default'}
              variant='outlined'
            />
            <Box sx={{ flex: 1 }} />
            {canEdit && (
              <>
                <CPEditIconButton onClick={onEdit} />
                <CPDeleteIconButton onClick={onDelete} />
              </>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 3 }}>
            <Box>
              <Typography variant='body2' color='text.secondary'>
                {society.address}
                {countryLabel ? `, ${countryLabel}` : ''}
              </Typography>
              {society.constitution_date && (
                <Typography variant='caption' color='text.secondary' display='block'>
                  {t('Constitution Date')}: {new Date(society.constitution_date).toLocaleDateString()}
                </Typography>
              )}
              {society.maintenance_cost !== undefined && society.maintenance_cost !== null && society.maintenance_cost !== '' && (
                <Typography variant='caption' color='text.secondary' display='block'>
                  {t('Maintenance Cost')}: ${Number(society.maintenance_cost).toFixed(2)}
                </Typography>
              )}
            </Box>

            {literalDoc && (
              <Box
                onClick={() => setLiteralPreviewOpen(true)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  minWidth: 110,
                  px: 2,
                  py: 1.5,
                  borderRadius: 2,
                  bgcolor: window.$brand === 'PRB' ? '#17afb6' : '#102a46',
                  color: '#fff',
                  cursor: 'pointer',
                  boxShadow: 2,
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
                  '&:hover': {
                    bgcolor: window.$brand === 'PRB' ? '#128a90' : '#0b1d30',
                    boxShadow: 4,
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <DescriptionOutlinedIcon sx={{ fontSize: 40 }} />
                <Typography variant='caption' sx={{ fontWeight: 700, textAlign: 'center' }}>
                  {t('view_literal')}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box
        onClick={() => setCompliancesOpen((prev) => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          userSelect: 'none',
          mb: compliancesOpen ? 1.5 : 0
        }}
      >
        <Typography variant='subtitle2' color='text.secondary'>
          {t('Compliance')} ({society.compliances.length})
        </Typography>
        <IconButton
          size='small'
          onClick={(e) => {
            e.stopPropagation()
            setCompliancesOpen((prev) => !prev)
          }}
          aria-label={compliancesOpen ? t('Collapse') : t('Expand')}
          sx={{
            transform: compliancesOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s'
          }}
        >
          <ExpandMoreRoundedIcon fontSize='small' />
        </IconButton>
      </Box>

      <Collapse in={compliancesOpen} timeout='auto' unmountOnExit>
        <Grid container spacing={2}>
          {society.compliances.map((c) => (
            <Grid item xs={12} sm={6} key={c.id}>
              <ComplianceItemCard
                compliance={c}
                society={society}
                canEdit={canEdit}
                onEdit={() => setEditingCompliance(c)}
                onDelete={() => setDeleteComplianceCandidate(c)}
              />
            </Grid>
          ))}
          {canEdit && (
            <Grid item xs={12} sm={6}>
              <Box
                onClick={() => setAssignOpen(true)}
                sx={{
                  height: '100%',
                  minHeight: 120,
                  borderRadius: 2,
                  border: '1px dashed',
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  color: 'text.secondary',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'text.secondary', color: 'text.primary' }
                }}
              >
                <AddRoundedIcon />
                <Typography variant='body2'>{t('assign_compliance')}</Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Collapse>

      <AssignComplianceDialog
        open={assignOpen}
        society={society}
        onClose={() => setAssignOpen(false)}
        onAssigned={() => {
          setAssignOpen(false)
          onChange()
        }}
      />

      <ComplianceEditDialog
        open={!!editingCompliance?.id}
        compliance={editingCompliance}
        onClose={() => setEditingCompliance(null)}
        onSave={handleUpdateCompliance}
      />

      <DeleteDialog
        item={deleteComplianceCandidate}
        title={t('delete_compliance')}
        caption={t('delete_society_compliance_with_documents_caption')}
        targetFunc={handleDeleteCompliance}
        handleDialogClose={() => setDeleteComplianceCandidate({})}
      />

      <DocumentPreviewDialog
        open={literalPreviewOpen}
        doc={literalDoc ? { id: society.id, filename: literalDoc.filename } : null}
        fetchUrl={(d) => generateSocietyLiteralDocumentUrl(d.id)}
        onClose={() => setLiteralPreviewOpen(false)}
      />
    </Card>
  )
}
